// src/pages/app/VendorsPage.jsx — Vendor Directory Management
import { useEffect, useState } from 'react';
import { vendorsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Loader2, Save, X, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const CATEGORIES = ['airline', 'hotel', 'cab', 'train', 'insurance', 'forex', 'other'];
const CATEGORY_ICONS = { airline: '✈️', hotel: '🏨', cab: '🚕', train: '🚆', insurance: '🛡️', forex: '💱', other: '📎' };

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";

const emptyVendor = { name: '', category: 'airline', contact_name: '', contact_email: '', contact_phone: '', negotiated_rate: '', currency: 'INR', notes: '' };

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyVendor);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await vendorsApi.list({ category: categoryFilter || undefined });
      setVendors(data);
    } catch { toast.error('Failed to load vendors'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [categoryFilter]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, negotiated_rate: parseFloat(form.negotiated_rate || 0) };
      if (editingId) {
        await vendorsApi.update(editingId, payload);
        toast.success('Vendor updated!');
      } else {
        await vendorsApi.create(payload);
        toast.success('Vendor added!');
      }
      setShowForm(false); setEditingId(null); setForm(emptyVendor);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleEdit = (vendor) => { setForm({ ...vendor }); setEditingId(vendor.id); setShowForm(true); };

  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await vendorsApi.delete(deleteId);
      toast.success('Vendor removed');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
    finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const filtered = vendors.filter(v =>
    v.name?.toLowerCase().includes(search.toLowerCase()) ||
    v.contact_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">Vendor Directory</h1>
          <p className="text-slate-500 text-sm mt-1">Manage preferred vendors and negotiated rates</p>
        </div>
        <button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(emptyVendor); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Add Vendor
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base">{editingId ? 'Edit Vendor' : 'Add New Vendor'}</CardTitle>
              <button onClick={() => { setShowForm(false); setEditingId(null); }}
                className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Vendor Name *</label>
                <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Air India" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Category</label>
                <Select
                  value={form.category}
                  onValueChange={(val) => setForm(f => ({ ...f, category: val }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>
                        {CATEGORY_ICONS[c]} {c.charAt(0).toUpperCase() + c.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Contact Name</label>
                <input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="Rajiv Kumar" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Contact Email</label>
                <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="contact@vendor.com" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Phone</label>
                <input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} placeholder="+91 98765 43210" className={inputCls} />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Negotiated Rate</label>
                  <input type="number" min="0" value={form.negotiated_rate} onChange={e => setForm(f => ({ ...f, negotiated_rate: e.target.value }))} placeholder="0" className={inputCls} />
                </div>
                <div className="w-[100px]">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Currency</label>
                  <Select
                    value={form.currency}
                    onValueChange={(val) => setForm(f => ({ ...f, currency: val }))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['INR', 'USD', 'EUR', 'GBP'].map(c => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Notes</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Contract details, special terms..." className={`${inputCls} resize-none`} />
              </div>
              <div className="sm:col-span-2 flex gap-3">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }}
                  className="px-5 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingId ? 'Update Vendor' : 'Add Vendor'}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendors..."
            className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <Select value={categoryFilter || 'all'} onValueChange={val => setCategoryFilter(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => (
              <SelectItem key={c} value={c}>
                {CATEGORY_ICONS[c]} {c.charAt(0).toUpperCase() + c.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vendors Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" /></div>
          ) : !filtered.length ? (
            <div className="text-center py-16 text-slate-400 text-sm">
              No vendors found.<br />
              <button onClick={() => { setShowForm(true); }} className="mt-2 text-blue-600 hover:underline">Add your first vendor →</button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="px-3 py-2 sm:px-4 sm:py-2.5 lg:px-6 lg:py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between text-xs text-slate-500 font-medium">
                <span>Showing <strong>{filtered.length}</strong> vendor partner{filtered.length === 1 ? '' : 's'}</span>
              </div>
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    <th className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">Vendor Name</th>
                    <th className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">Category</th>
                    <th className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">Contact Person</th>
                    <th className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">Negotiated Rate</th>
                    <th className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">Notes</th>
                    <th className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map(v => (
                    <tr key={v.id} className="hover:bg-slate-50/80 transition-colors group">
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{v.name}</td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5">
                        <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-semibold border border-blue-200">
                          {CATEGORY_ICONS[v.category]} {v.category}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-600 font-medium">
                        <div className="font-semibold text-slate-900">{v.contact_name || '—'}</div>
                        {v.contact_email && <div className="text-slate-400">{v.contact_email}</div>}
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 font-semibold text-slate-900 whitespace-nowrap">
                        {parseFloat(v.negotiated_rate) > 0 ? `${v.currency} ${parseFloat(v.negotiated_rate).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-xs text-slate-500 max-w-[200px] truncate">{v.notes || '—'}</td>
                      <td className="px-3 py-2.5 sm:px-4 sm:py-3 lg:px-6 lg:py-3.5 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => handleEdit(v)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Edit Vendor">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteClick(v.id)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                            title="Remove Vendor">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <div className="w-11 h-11 rounded-full bg-red-100 text-red-600 flex items-center justify-center mb-1 mx-auto sm:mx-0">
              <Trash2 className="w-5 h-5" />
            </div>
            <AlertDialogTitle className="text-lg sm:text-xl font-bold text-slate-900">
              Remove Vendor
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              Are you sure you want to remove this vendor directory record? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2 sm:gap-0">
            <AlertDialogCancel
              onClick={() => setShowDeleteModal(false)}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white font-medium"
            >
              Remove Vendor
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
